import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Trophy } from "lucide-react";
import { toast } from "sonner";

const fmtMoney = (v) => v >= 1e6 ? `$${(v / 1e6).toFixed(2)}M` : `$${Math.round(v / 1e3).toLocaleString()}k`;
const pctColour = (v) => v >= 70 ? "text-emerald-700" : v >= 45 ? "text-amber-700" : "text-rose-700";
const barColour = (v) => v >= 70 ? "bg-emerald-500" : v >= 45 ? "bg-amber-500" : "bg-rose-500";

const CatCell = ({ score, weight }) => {
  const pct = weight ? (score / weight) * 100 : 0;
  const tone = pct >= 70 ? "bg-emerald-100 text-emerald-800" : pct >= 45 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800";
  return (
    <span className={`inline-block min-w-14 text-center px-1.5 py-0.5 rounded text-[11px] font-semibold tabular-nums ${tone}`}>
      {score.toFixed(1)}<span className="font-normal opacity-60">/{weight}</span>
    </span>
  );
};

export const BUCompare = ({ month }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!month) return;
    setBusy(true);
    api.get("/qms/compare", { params: { month } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load BU comparison"))
      .finally(() => setBusy(false));
  }, [month]);

  if (busy && !data) {
    return <div className="h-64 flex items-center justify-center text-slate-400 text-sm">
      <Loader2 className="w-4 h-4 animate-spin mr-2" /> Scoring all business units…
    </div>;
  }
  if (!data) return null;
  const w = data.category_weights || {};
  const best = data.rows[0];

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="bu-compare">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm font-semibold text-slate-800">All business units · {data.month}</div>
        {best && best.total_score_pct > 0 && (
          <div className="text-xs text-slate-500 flex items-center gap-1.5">
            <Trophy className="w-3.5 h-3.5 text-amber-500" /> Top: <span className="font-semibold text-slate-800">{best.bu_code}</span> at {best.total_score_pct.toFixed(1)}%
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
              <th className="px-3 py-2 w-8">#</th>
              <th className="px-3 py-2">Business unit</th>
              <th className="px-3 py-2 w-56">Total score</th>
              <th className="px-2 py-2 text-center">Financial ({w.financial}%)</th>
              <th className="px-2 py-2 text-center">Safety ({w.safety}%)</th>
              <th className="px-2 py-2 text-center">Customer ({w.customer}%)</th>
              <th className="px-2 py-2 text-center">Performance ({w.performance}%)</th>
              <th className="px-3 py-2 text-right">Sales</th>
              <th className="px-3 py-2 text-right">GP%</th>
              <th className="px-3 py-2 text-right">Util%</th>
              <th className="px-3 py-2 text-right">CSAT</th>
              <th className="px-3 py-2 text-right">LTI</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {data.rows.map((r, i) => (
              <tr key={r.business_unit_id} className="hover:bg-slate-50/60" data-testid={`compare-row-${r.bu_code}`}>
                <td className="px-3 py-2 text-slate-400 text-xs tabular-nums">{i + 1}</td>
                <td className="px-3 py-2">
                  <span className="font-mono text-xs font-semibold text-slate-800">{r.bu_code}</span>
                  <span className="block text-[10px] text-slate-400 truncate max-w-40">{r.bu_name}</span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full ${barColour(r.total_score_pct)}`}
                           style={{ width: `${Math.min(100, r.total_score_pct)}%` }} />
                    </div>
                    <span className={`text-xs font-bold tabular-nums w-12 text-right ${pctColour(r.total_score_pct)}`}>
                      {r.total_score_pct.toFixed(1)}%
                    </span>
                  </div>
                </td>
                <td className="px-2 py-2 text-center"><CatCell score={r.categories.financial} weight={w.financial} /></td>
                <td className="px-2 py-2 text-center"><CatCell score={r.categories.safety} weight={w.safety} /></td>
                <td className="px-2 py-2 text-center"><CatCell score={r.categories.customer} weight={w.customer} /></td>
                <td className="px-2 py-2 text-center"><CatCell score={r.categories.performance} weight={w.performance} /></td>
                <td className="px-3 py-2 text-right text-xs tabular-nums">{r.sales ? fmtMoney(r.sales) : "—"}</td>
                <td className="px-3 py-2 text-right text-xs tabular-nums">{r.gp_pct != null ? `${r.gp_pct}%` : "—"}</td>
                <td className="px-3 py-2 text-right text-xs tabular-nums">{r.utilisation != null ? `${(r.utilisation * 100).toFixed(0)}%` : "—"}</td>
                <td className="px-3 py-2 text-right text-xs tabular-nums">{r.csat != null ? r.csat.toFixed(1) : "—"}</td>
                <td className="px-3 py-2 text-right text-xs tabular-nums">
                  {r.lti != null ? (
                    <span className={r.lti > 0 ? "text-rose-700 font-semibold" : ""}>{r.lti}</span>
                  ) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[10px] text-slate-400 border-t border-slate-100">
        Same scoring as each BU's dashboard — optional KPIs (CSAT, utilisation, discipline, resolution days) only count in months where data exists.
      </div>
    </div>
  );
};
